import { Controller } from '@hotwired/stimulus'
import { cache, type TurboBeforeRenderEvent, type TurboFrameMissingEvent } from '@hotwired/turbo'

// A live region that arrives filled with the new frame announces nothing; it has to change after it exists.
const ANNOUNCE_DELAY_MS = 150

export default class extends Controller<HTMLElement> {
  static override targets = ['frame', 'status', 'week', 'strip', 'day', 'panel']
  static override values = { origin: String }

  declare readonly originValue: string
  declare readonly frameTarget: HTMLElement
  declare readonly statusTarget: HTMLElement
  declare readonly hasWeekTarget: boolean
  declare readonly weekTarget: HTMLElement
  declare readonly hasStripTarget: boolean
  declare readonly stripTarget: HTMLElement
  declare readonly dayTargets: HTMLElement[]
  declare readonly panelTargets: HTMLElement[]

  private focusKey: string | null = null
  private summary: string | null | undefined

  override connect() {
    cache.exemptPageFromPreview()
  }

  remember({ currentTarget }: Event) {
    if (currentTarget instanceof HTMLElement)
      this.focusKey = currentTarget.dataset.agendaFocus ?? null
  }

  pend({ currentTarget }: Event) {
    if (currentTarget instanceof HTMLElement) currentTarget.dataset.pending = 'true'
    if (this.hasWeekTarget) this.weekTarget.dataset.pending = 'true'
  }

  ask({ currentTarget }: Event) {
    if (!(currentTarget instanceof HTMLInputElement)) return
    this.focusKey = 'modality'
    currentTarget.form?.requestSubmit()
  }

  hold() {
    const focused = document.activeElement
    if (this.focusKey !== null || !(focused instanceof HTMLElement)) return
    if (!this.frameTarget.contains(focused)) return

    this.focusKey =
      focused.dataset.agendaFocus ??
      (focused.matches('input[name="modality"]') ? 'modality' : 'range')
  }

  settle() {
    this.centreStrip()
    this.announce()
    this.restoreFocus()
  }

  missing(event: TurboFrameMissingEvent) {
    event.preventDefault()
    void event.detail.visit(event.detail.response, {})
  }

  forget() {
    this.frameTarget.removeAttribute('complete')
  }

  carry({ detail }: TurboBeforeRenderEvent) {
    const next = detail.newBody.querySelector<HTMLElement>(`turbo-frame#${this.frameTarget.id}`)
    const current = this.frameTarget.getAttribute('src')
    if (next && current && next.getAttribute('src') === this.originValue)
      next.setAttribute('src', current)
  }

  pickDay({ currentTarget }: Event) {
    if (!(currentTarget instanceof HTMLElement)) return
    for (const day of this.dayTargets)
      day.setAttribute('aria-pressed', String(day === currentTarget))
    for (const panel of this.panelTargets)
      panel.hidden = panel.dataset.date !== currentTarget.dataset.date
  }

  dismiss({ currentTarget }: Event) {
    if (currentTarget instanceof HTMLElement) currentTarget.closest('.agenda__notice')?.remove()
  }

  arrived({ animationName }: AnimationEvent) {
    if (animationName === 'agenda-arrive' && this.hasWeekTarget)
      delete this.weekTarget.dataset.arriving
  }

  // Only scrollLeft, never scrollIntoView: the strip must never move the document.
  private centreStrip() {
    if (!this.hasStripTarget) return
    const strip = this.stripTarget
    const day = this.dayTargets.find((each) => each.getAttribute('aria-pressed') === 'true')
    if (day === undefined || strip.clientWidth === 0) return

    const offset = day.getBoundingClientRect().left - strip.getBoundingClientRect().left
    strip.scrollLeft += offset - (strip.clientWidth - day.clientWidth) / 2
  }

  private announce() {
    const summary =
      this.element.querySelector<HTMLElement>('[data-summary]')?.dataset.summary ?? null
    const first = this.summary === undefined
    if (summary === this.summary) return

    this.summary = summary
    if (first || summary === null) return
    window.setTimeout(() => {
      this.statusTarget.textContent = summary
    }, ANNOUNCE_DELAY_MS)
  }

  private restoreFocus() {
    const key = this.focusKey
    this.focusKey = null
    if (key === null) return

    const wanted =
      key === 'modality'
        ? this.element.querySelector<HTMLElement>('input[name="modality"]:checked')
        : this.shown(`[data-agenda-focus="${key}"]`)
    ;(wanted ?? this.shown('[data-agenda-focus="range"]'))?.focus()
  }

  private shown(selector: string): HTMLElement | null {
    return (
      [...this.element.querySelectorAll<HTMLElement>(selector)].find((each) =>
        each.checkVisibility(),
      ) ?? null
    )
  }
}
